import { PlanWarnings } from '@constants/pricing';
import { createSlice } from '@reduxjs/toolkit';
import { replacePricingPlanKeyword } from '@utils/common';

interface ApiError {
  endpoint: string;
  method: string;
  payload: string;
  response: any;
  errorStatus: string;
}

interface FeatureList {
  downloadCleanedFile: boolean;
  downloadReport: boolean;
  combineAppendFile: boolean;
  autoCategorization: boolean;
  removeWaterMark: boolean;
  chartInsights: boolean;
  projectInsights: boolean;
  restApi: boolean;
  squirby: boolean;
  manualUpdate: boolean;
  autoAi: boolean;
  sizeLimit: number | null;
  fileFormats: string[] | null;
  projectLimit: number;
  squirbyLimit: number;
  insightsLimit: number;
  gSheetUpdateLimit: number;
  apiUpdateLimit: number;
  apiUploadLimit: number;
}

export interface PlanError {
  title: string;
  sub_title: string;
  feature_name?: string;
  modal_name?: string;
  button_text: string;
}

export interface FreePlanFeatures {
  SIZE_LIMIT: number | null;
  ROW_LIMIT: number;
  COLUMNS_LIMIT: number;
  PROJECT_LIMIT: number | null;
  FILE_FORMATS: string[];
  PAID_FEATURES: string[];
  SQB_LIMIT: number | null;
  MAX_MONTHLY_API_POST_REQUESTS: number | null;
}

export interface LitePlanFeatures {
  FILE_FORMATS?: string[];
  MAX_MONTHLY_DATA_UPDATE_LIMIT?: number;
  MAX_MONTHLY_INSIGHTS_LIMIT?: number;
  MAX_MONTHLY_RECALCULATION_LIMIT?: number;
  PAID_FEATURES?: string[];
  PROJECT_LIMIT_MONTHLY?: number;
  ROW_LIMIT: number;
  COLUMNS_LIMIT: number;
  SIZE_LIMIT: number;
  SQB_LIMIT_MONTHLY?: number;
}

interface AppState {
  apiErrorLog: {
    [key: string]: ApiError;
  };
  planUpdateErrors: PlanError | null;
  currentPlanFeatures: FeatureList;
  subKeyFlag: boolean;
  combineKeyFlag: boolean;
  planValidityText: { type: PlanWarnings; value: string };
  freePlanFeatures: FreePlanFeatures;
  litePlanFeatures: LitePlanFeatures;
}

const initialState: AppState = {
  apiErrorLog: {},
  planUpdateErrors: null,
  currentPlanFeatures: {
    downloadCleanedFile: false,
    downloadReport: false,
    combineAppendFile: false,
    autoCategorization: false,
    removeWaterMark: false,
    chartInsights: false,
    projectInsights: false,
    restApi: false,
    squirby: false,
    manualUpdate: false,
    autoAi: false,
    sizeLimit: 0,
    fileFormats: [],
    projectLimit: 0,
    squirbyLimit: 0,
    insightsLimit: 0,
    gSheetUpdateLimit: 0,
    apiUpdateLimit: 0,
    apiUploadLimit: 0
  },
  subKeyFlag: false,
  combineKeyFlag: false,
  planValidityText: { type: PlanWarnings.NONE, value: '' },
  freePlanFeatures: {
    SIZE_LIMIT: null,
    ROW_LIMIT: 0,
    COLUMNS_LIMIT: 0,
    PROJECT_LIMIT: null,
    FILE_FORMATS: [],
    PAID_FEATURES: [],
    SQB_LIMIT: null,
    MAX_MONTHLY_API_POST_REQUESTS: null
  },
  litePlanFeatures: {
    SIZE_LIMIT: 0,
    ROW_LIMIT: 0,
    COLUMNS_LIMIT: 0
  }
};

export const AppData = createSlice({
  name: 'AppData',
  initialState,
  reducers: {
    setApiErrorLog: (state: AppState, { payload }: { payload: ApiError }) => {
      const data = state?.apiErrorLog
        ? { ...state.apiErrorLog, [payload.endpoint]: payload }
        : { [payload.endpoint]: payload };

      return { ...state, apiErrorLog: data };
    },
    unsetApiErrorLog: (state) => ({ ...state, apiErrorLog: {} }),
    setPlanUpdateErrors: (
      state: AppState,
      { payload }: { payload: PlanError }
    ) => ({
      ...state,
      planUpdateErrors: {
        ...payload,
        sub_title: replacePricingPlanKeyword(payload.sub_title),
        title: replacePricingPlanKeyword(payload.title),
        feature_name: payload.feature_name,
        modal_name: payload.modal_name
      }
    }),
    resetPlanErrors: (state) => ({ ...state, planUpdateErrors: null }),
    resetAppData: () => initialState,
    setCurrentPlanFeatures: (
      state: AppState,
      { payload }: { payload: FeatureList }
    ) => ({ ...state, currentPlanFeatures: payload }),
    setPlanValidityText: (
      state: AppState,
      { payload }: { payload: { type: PlanWarnings; value: string } }
    ) => ({ ...state, planValidityText: payload }),
    setPlanFeatureFlag: (
      state: AppState,
      { payload }: { payload: boolean }
    ) => ({ ...state, subKeyFlag: payload }),
    setCombineFeatureFlag: (
      state: AppState,
      { payload }: { payload: boolean }
    ) => ({ ...state, combineKeyFlag: payload }),
    setFreePlanFeatures: (
      state: AppState,
      { payload }: { payload: FreePlanFeatures }
    ) => ({ ...state, freePlanFeatures: payload }),
    setLitePlanFeatures: (
      state: AppState,
      { payload }: { payload: LitePlanFeatures }
    ) => ({ ...state, litePlanFeatures: payload })
  }
});

export const {
  setApiErrorLog,
  unsetApiErrorLog,
  resetAppData,
  setPlanUpdateErrors,
  resetPlanErrors,
  setCurrentPlanFeatures,
  setPlanValidityText,
  setPlanFeatureFlag,
  setCombineFeatureFlag,
  setFreePlanFeatures,
  setLitePlanFeatures
} = AppData.actions;

export default AppData.reducer;
